import { Add, EditOutlined } from "@mui/icons-material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import { debounce } from "lodash";
import MUIDataTable from "mui-datatables";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";

import { getPlayoffBuckets } from "../../apis/Playoff";
import { FormDrawer } from "../../components/Form/FormDrawer";
import { FullHeightLoader } from "../../components/Loader/FullHeightLoader";
import { PlayoffBucketDrawer } from "../../components/Playoff/PlayoffBucket/PlayoffBucketDrawer";
import { PlayoffBucketPlayoffDrawer } from "../../components/Playoff/PlayoffBucket/PlayoffBucketPlayoffDrawer";

export default function PlayoffBucketListing() {
  // =========================
  // State
  // =========================

  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const [buckets, setBuckets] = useState([]);
  const [count, setCount] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState(null);

  const [openPlayoffDrawer, setOpenPlayoffDrawer] = useState(false);
  const [selectedBucketForPlayoff, setSelectedBucketForPlayoff] =
    useState(null);

  // =========================
  // Debounced Search
  // =========================

  const debouncedFetch = useMemo(
    () =>
      debounce((value) => {
        setSearchText(value);
        setPage(0);
      }, 500),
    [],
  );

  // =========================
  // Effects
  // =========================

  const fetchBuckets = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getPlayoffBuckets(rowsPerPage, page + 1, {
        search: searchText,
      });

      setBuckets(response.results);
      setCount(response.count);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [page, rowsPerPage, searchText]);

  useEffect(() => {
    fetchBuckets();
  }, [fetchBuckets]);

  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  // =========================
  // API Calls
  // =========================

  const reloadTable = async () => {
    await fetchBuckets();
  };

  // =========================
  // Drawer Handlers
  // =========================

  const handleAdd = () => {
    setSelectedBucket(null);
    setOpenDrawer(true);
  };

  const handleEdit = (bucket) => {
    setSelectedBucket(bucket);
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setSelectedBucket(null);
  };

  const handleAddPlayoffs = (bucket) => {
    setSelectedBucketForPlayoff(bucket);
    setOpenPlayoffDrawer(true);
  };

  const handleClosePlayoffDrawer = () => {
    setOpenPlayoffDrawer(false);
    setSelectedBucketForPlayoff(null);
  };

  // =========================
  // Table Helpers
  // =========================

  const centerColumn = {
    setCellProps: () => ({
      style: {
        textAlign: "center",
      },
    }),
    setCellHeaderProps: () => ({
      style: {
        textAlign: "center",
      },
    }),
  };

  // =========================
  // Table Columns
  // =========================

  const columns = [
    {
      name: "name",
      label: "Bucket Name",
      options: centerColumn,
    },
    {
      name: "is_active",
      label: "Status",
      options: {
        ...centerColumn,
        customBodyRender: (value) => (
          <Chip
            size="small"
            label={value ? "Active" : "Inactive"}
            color={value ? "success" : "default"}
          />
        ),
      },
    },
    {
      name: "playoffs_count",
      label: "PlayOffs",
      options: centerColumn,
    },
    {
      name: "id",
      label: "Action",
      options: {
        ...centerColumn,
        sort: false,
        filter: false,
        customBodyRender: (_, tableMeta) => {
          const bucket = buckets[tableMeta.rowIndex];

          return (
            <Box display="flex" justifyContent="center" gap={1}>
              <IconButton color="primary" onClick={() => handleEdit(bucket)}>
                <EditOutlined />
              </IconButton>

              <IconButton
                color="primary"
                onClick={() => handleAddPlayoffs(bucket)}
              >
                <AddCircleOutlineIcon color="primary" />
              </IconButton>
            </Box>
          );
        },
      },
    },
  ];

  // =========================
  // Initial Loader
  // =========================

  if (initialLoading) {
    return <FullHeightLoader />;
  }

  // =========================
  // Render
  // =========================

  return (
    <>
      <ToastContainer />

      <Box p={2}>
        <Toolbar>
          <Typography variant="h4" noWrap sx={{ flexGrow: 1 }}>
            PlayOff Buckets
          </Typography>

          <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
            Add Bucket
          </Button>
        </Toolbar>

        <Box sx={{ px: 22, py: 2 }}>
          <MUIDataTable
            title=""
            data={buckets}
            columns={columns}
            options={{
              serverSide: true,

              page,
              count,

              rowsPerPage,
              rowsPerPageOptions: [10, 25, 50],

              selectableRows: "none",
              responsive: "standard",
              download: false,
              print: false,
              filter: false,
              viewColumns: false,
              sort: false,

              search: true,
              searchText: searchInput,

              onSearchChange: (text) => {
                setSearchInput(text || "");
                debouncedFetch(text || "");
              },

              onTableChange: (action, tableState) => {
                switch (action) {
                  case "changePage":
                    setPage(tableState.page);
                    break;

                  case "changeRowsPerPage":
                    setRowsPerPage(tableState.rowsPerPage);
                    setPage(0);
                    break;

                  default:
                    break;
                }
              },
            }}
            isLoading={loading}
          />
        </Box>
      </Box>

      <FormDrawer
        open={openDrawer}
        setEditOpen={handleCloseDrawer}
        loading={drawerLoading}
      >
        <PlayoffBucketDrawer
          bucket={selectedBucket}
          setEditOpen={handleCloseDrawer}
          reloadTable={reloadTable}
          setLoading={setDrawerLoading}
        />
      </FormDrawer>

      <FormDrawer
        open={openPlayoffDrawer}
        setEditOpen={handleClosePlayoffDrawer}
        loading={drawerLoading}
      >
        <PlayoffBucketPlayoffDrawer
          bucket={selectedBucketForPlayoff}
          setEditOpen={handleClosePlayoffDrawer}
          reloadTable={reloadTable}
          setLoading={setDrawerLoading}
        />
      </FormDrawer>
    </>
  );
}
